import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import WriteReplyBox from 'src/modules/community/singlePost/components/replies/writeReplyBox';
import ReplyItemBody from 'src/modules/community/singlePost/components/replies/replyItemBody';
import ReplyItemHeader from 'src/modules/community/singlePost/components/replies/replyItemHeader';
import { HIDDEN_USERNAME } from 'src/constants/hiddenUser';
import { RestructuredReply } from 'src/constants/types';
import { textWideSpacing } from 'styles-js/mixins/typography';

type ReplyItemProps = {
  indent: number,
  inReplyToUsername?: string,
  currentResponse?: RestructuredReply, // because it's using restructured responses
}

export default function InteractiveReplyItem({ inReplyToUsername, currentResponse, indent }: ReplyItemProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const submittedReplyId = useSelector(({ pages }) => pages.singlePost?.composeReply?.submittedReplyId);

  const responseRef = useScrolling(isEditing, isReplying, currentResponse.id);

  useEffect(() => {
    if (submittedReplyId === currentResponse.id) {
      setIsReplying(false);
      setIsEditing(false);
    }
  }, [submittedReplyId, currentResponse.id]);

  const currentResponseUsername = !currentResponse.author.isDeleted && !currentResponse.author.isHidden
    ? currentResponse.author.username
    : i18n.t(HIDDEN_USERNAME);

  return (
    <Response indent={indent} ref={responseRef}>
      <ReplyItemHeader
        author={currentResponse.author}
        dateCreated={currentResponse.dateCreated}
        inReplyToUsername={inReplyToUsername}
        onCancel={isEditing ? () => setIsEditing(false) : null}
      />

      {isEditing ? (
        <WriteReplyBox
          replyData={{
            responseId: currentResponse.id,
            parentId: currentResponse?.parentPostId,
            body: currentResponse.body,
            imageHash: currentResponse?.postImageHash,
            imageUrl: currentResponse?.imageUrl,
            imageDescription: currentResponse.imageDescription,
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <ReplyItemBody response={currentResponse} startEditing={() => setIsEditing(true)} startReplying={() => setIsReplying(true)}/>
      )}

      {isReplying && (
        <WriteReplyBox
          inReplyToUsername={currentResponseUsername}
          replyData={{ responseId: currentResponse?.id }}
          onCancel={() => setIsReplying(false)}
        />
      )}

      {currentResponse.responses?.map((response) => (
        <InteractiveReplyItem
          currentResponse={response as unknown as RestructuredReply}
          indent={response.indent}
          inReplyToUsername={currentResponseUsername}
          key={response.id}
        />
      ))}
    </Response>
  );
}

function useScrolling(isEditing: boolean, isReplying: boolean, currentResponseId: number) {
  const { query } = useRouter();
  const responseId = Number(query?.responses as string);
  const isLoading = useSelector(({ pages }) => pages.singlePost.isLoading);
  const submittedReplyId = useSelector(({ pages }) => pages.singlePost?.composeReply?.submittedReplyId);

  const responseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading || (responseId !== currentResponseId && submittedReplyId !== currentResponseId)) return;
    const timeout = setTimeout(() => {
      responseRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300); // wait for the response to load
    return () => clearTimeout(timeout);
  }, [responseId, currentResponseId, isLoading, submittedReplyId]);

  useEffect(() => {
    if (isEditing || isReplying) {
      responseRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isEditing, isReplying]);

  return responseRef;
}

const Response = styled.div<{ indent: number }>`
  content-visibility: auto;
  contain-intrinsic-size: auto 350px;
  scroll-margin-top: 60px;
  ${({ indent }) => indent === 1 && css`
    margin-top: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colorGreyLight};
  `}
  ${({ indent }) => indent > 1 && indent < 4 && css`
    margin-top: 15px;
    padding-left: 25px;
    border-left: 1px solid ${({ theme }) => theme.colorGreyLight};
    @media (${({ theme }) => theme.underScreenSmall}) {
      padding-left: 15px;
    }
    em-emoji-picker {
      @media (max-width: 359px) {
        left: -32px !important; // important to overwrite styling in emojiPicker.tsx
      }
    }
  `}
  ${({ indent }) => indent > 3 && css`
    margin-top: 15px;
    em-emoji-picker {
      @media (max-width: 359px) {
        left: -48px !important; // important to overwrite styling in emojiPicker.tsx
      }
    }
  `}

  p {
    ${textWideSpacing}
    overflow-wrap: break-word;
    margin: 0 0 10px;
    &:last-child {
      margin: 0;
    }
  }
`;
